import { useState, type FormEvent } from 'react';
import { BLAZE_NAME, LIMITS, serverProfileSchema } from '@clanfire/shared';
import { serverApi } from '@/api/endpoints';
import { ProfileBio, ProfileCard } from '@/components/profile/ProfileCard';
import { BannerPicker, useObjectUrl } from '@/components/settings/ProfileEditor';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Fire } from '@/components/ui/icons';
import { TextArea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useSubmit, validate } from '@/hooks/useForm';
import { useAuth } from '@/stores/auth';
import { useHasBlaze } from '@/stores/blaze';
import { useServers } from '@/stores/servers';
import { toast, useUi } from '@/stores/ui';
import { ImagePicker } from './ImagePicker';

/** Your avatar, banner and bio for one server (a Blaze perk). */
export function ServerProfileModal({ serverId, onClose }: { serverId: string; onClose: () => void }) {
  const me = useAuth((s) => s.user)!;
  const server = useServers((s) => s.servers[serverId]);
  const saved = server?.me.profile ?? null;
  const hasBlaze = useHasBlaze();
  const [bio, setBio] = useState(saved?.bio ?? '');
  const [avatar, setAvatar] = useState<Blob | null | undefined>(undefined);
  const [banner, setBanner] = useState<Blob | null | undefined>(undefined);
  const avatarPreview = useObjectUrl(avatar);
  const bannerPreview = useObjectUrl(banner);
  const { loading, error, fields, setFields, run } = useSubmit();

  const dirty = avatar !== undefined || banner !== undefined || bio !== (saved?.bio ?? '');
  const preview = {
    ...me,
    avatarUrl: avatar === undefined ? (saved?.avatarUrl ?? me.avatarUrl) : (avatarPreview ?? me.avatarUrl),
    bannerUrl: banner === undefined ? (saved?.bannerUrl ?? me.bannerUrl) : (bannerPreview ?? me.bannerUrl),
    bio: bio.trim() || me.bio,
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const { data, fields: invalid } = validate(serverProfileSchema, { bio });
    if (!data) return setFields(invalid);
    const done = await run(async () => {
      let member = null;
      if (data.bio !== (saved?.bio ?? null)) member = await serverApi.setProfileBio(serverId, data.bio);
      if (avatar !== undefined) member = avatar ? await serverApi.setProfileImage(serverId, 'avatar', avatar) : await serverApi.removeProfileImage(serverId, 'avatar');
      if (banner !== undefined) member = banner ? await serverApi.setProfileImage(serverId, 'banner', banner) : await serverApi.removeProfileImage(serverId, 'banner');
      if (member) useServers.getState().upsertMember(member);
      return true;
    });
    if (!done) return;
    toast.success(`Your profile for ${server?.name ?? 'this server'} is saved.`);
    onClose();
  };

  const reset = async () => {
    const member = await run(() => serverApi.resetProfile(serverId));
    if (!member) return;
    useServers.getState().upsertMember(member);
    toast.success('Back to your own profile here.');
    onClose();
  };

  if (!hasBlaze) {
    return (
      <Modal open onClose={onClose} title="Server profile" size="sm">
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#f97316] to-[#e11d48] text-white">
            <Fire size={24} aria-hidden />
          </span>
          <p className="text-sm text-fg-soft">
            With {BLAZE_NAME} you can have a different avatar, banner and bio in each server{server ? `, like one just for ${server.name}` : ''}.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {saved && (
              <Button variant="ghost" loading={loading} onClick={() => void reset()}>
                Remove my server profile
              </Button>
            )}
            <Button onClick={() => useUi.getState().openModal({ type: 'userSettings', section: 'blaze' })}>See Blaze</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Server profile"
      description={server ? `Only shown in ${server.name}. Anything you leave empty uses your own profile.` : undefined}
      size="lg"
      footer={
        <>
          {saved && (
            <Button variant="ghost" className="mr-auto" disabled={loading} onClick={() => void reset()}>
              Use my own profile
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="server-profile-form" loading={loading} disabled={!dirty}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid gap-5 md:grid-cols-[1fr_280px]">
        <form id="server-profile-form" onSubmit={save} className="flex flex-col gap-5">
          <div>
            <p className="mb-2 text-xs font-bold tracking-wide text-fg-soft uppercase">Avatar</p>
            <ImagePicker currentUrl={saved?.avatarUrl ?? null} fallback={<Avatar user={me} size={88} decorate={false} />} onChange={setAvatar} />
          </div>
          <div>
            <p className="mb-2 text-xs font-bold tracking-wide text-fg-soft uppercase">Banner</p>
            <BannerPicker url={banner === undefined ? (saved?.bannerUrl ?? null) : (bannerPreview ?? null)} color={me.accentColor} onChange={setBanner} />
          </div>
          <TextArea
            label="About me"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={LIMITS.bioMax}
            error={fields.bio}
            hint={`${bio.length}/${LIMITS.bioMax}`}
            placeholder={me.bio || 'Say something just for this server'}
          />
          {error && <p className="text-sm text-danger">{error}</p>}
        </form>
        <aside aria-label="Preview">
          <p className="mb-1.5 text-xs font-bold tracking-wide text-fg-soft uppercase">Preview</p>
          <div className="overflow-hidden rounded-2xl border border-line">
            <ProfileCard user={preview} presence="online" nickname={server?.me.nickname}>
              {preview.bio.trim() && (
                <div className="mt-4 rounded-xl bg-sunken p-4 text-sm">
                  <ProfileBio bio={preview.bio} />
                </div>
              )}
            </ProfileCard>
          </div>
        </aside>
      </div>
    </Modal>
  );
}
